import { Link, Navigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { FlexBox } from "../../Components/Boxes";
import Icon from "../../Components/Icons";
import { Text } from "../../Components/Text";
import { color, radius, shadow } from "../../Components/Theme";
import { BreadCrumbs } from "../../Components/BasicComponents/BreadCrumbs";
import usePermissionsController from "../../permissions";
import Clients from "../Team/Clients/Clients";
import { CARE_RECORDS_LABEL, careRecordClientPath, permittedCareRecordModules } from "../../constants/careRecords";

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

const ModuleCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  min-height: 80px;
  padding: 16px 20px;
  border: 1px solid ${color("border")};
  border-radius: ${radius("card")}px;
  background: ${color("surface")};
  box-shadow: ${shadow("card")};
  text-decoration: none;
  transition: box-shadow 150ms ease-out, border-color 150ms ease-out;
  &:hover {
    border-color: ${color("primary")};
    box-shadow: ${shadow("raised")};
  }
  &:focus-visible {
    outline: 2px solid ${color("primary")};
    outline-offset: 2px;
  }
`;

const ModuleBadge = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${radius("control")}px;
  background: ${color("primary-light")};
  color: ${color("primary")};
`;

const ModuleLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${color("text")};
`;

export default function CareRecords() {
  const { module: slug } = useParams()
  const permissions = usePermissionsController()
  const modules = permittedCareRecordModules(permissions)
  const selected = slug ? modules.find((m) => m.slug === slug) : null

  if (slug && !selected) return <Navigate to="/care-records" replace />

  return (
    <FlexBox
      $minHeight="70vh"
      $flexDirection="column"
      $alignItems="flex-start"
      $backgroundColor="#fff"
      $borderRadius="10px"
      $width="100%"
    >
      {selected && (
        <BreadCrumbs
          $crumbs={[
            { label: CARE_RECORDS_LABEL, path: "/care-records" },
            { label: selected.label, path: "" },
          ]}
        />
      )}

      <div style={{ width: "100%", boxSizing: "border-box", padding: selected ? "0 16px 16px" : "16px" }}>
        {selected ? (
          <>
            <Text $fontSize="heading-2" $marginBottom={1}>{selected.label}</Text>
            <Text $fontSize="body" $marginBottom={2}>Choose a client to open their {selected.label}.</Text>
            <Clients hideAdd clientPath={(clientId) => careRecordClientPath(clientId, selected)} />
          </>
        ) : (
          <>
            <Text $fontSize="heading-2" $marginBottom={3}>{CARE_RECORDS_LABEL}</Text>
            <ModuleGrid>
              {modules.map((m) => (
                <ModuleCard key={m.slug} to={`/care-records/${m.slug}`}>
                  <ModuleBadge aria-hidden="true">
                    <Icon $icon={m.icon} $size="24px" $color="inherit" />
                  </ModuleBadge>
                  <ModuleLabel>{m.label}</ModuleLabel>
                  <Icon $icon="ChevronRight" $size="20px" $color="text-muted" />
                </ModuleCard>
              ))}
            </ModuleGrid>
          </>
        )}
      </div>
    </FlexBox>
  );
}
